import api from '../utils/api';

const BASE = '/api/assignments';

/**
 * Fetch a single assignment, populated with scenario and students.
 * @route GET /api/assignments/assignment/:assignmentId
 * @param {string} assignmentId
 * @returns {Promise<Object>} The assignment document
 */
export const getAssignment = (assignmentId) =>
    api.get(`${BASE}/assignment/${assignmentId}`).then((r) => r.data);

/**
 * Fetch an assignment plus its student submissions (flattened log decisions).
 * @route GET /api/assignments/assignment/:assignmentId/with-submissions
 * @param {string} assignmentId
 * @returns {Promise<Object>} The assignment with a `submissions` array
 */
export const getAssignmentWithSubmissions = (assignmentId) =>
    api.get(`${BASE}/assignment/${assignmentId}/with-submissions`).then((r) => r.data);

/**
 * Fetch an assignment's viewer flags (disable table of contents / restart).
 * Works for unlogged users.
 * @route GET /api/assignments/getSkipStatus/:assignmentId
 * @param {string} assignmentId
 * @returns {Promise<{ToC: boolean, restart: boolean}|null>} Null if the assignment doesn't exist
 */
export const getAssignmentSkipStatus = (assignmentId) =>
    api.get(`${BASE}/getSkipStatus/${assignmentId}`).then((r) => r.data);

/**
 * Fetch all assignments for a classroom, newest first, each with a `logCount`.
 * @route GET /api/assignments/classroom/:classroomId
 * @param {string} classroomId
 * @returns {Promise<Object[]>} Assignment documents
 */
export const getClassroomAssignments = (classroomId) =>
    api.get(`${BASE}/classroom/${classroomId}`).then((r) => r.data);

/**
 * Create an assignment in a classroom and add it to each student's active assignments.
 *
 * With `assignToAll` (the default) the server expands the roster from the classroom's current
 * students and ignores `students`, so a page holding a roster from before a recent join still
 * assigns everyone. Read the returned `students` for who was actually assigned.
 * @route POST /api/assignments/:classroomId
 * @param {string} classroomId
 * @param {Object} assignmentData - Must include `scenario` (id); may include
 *   title, description, students, dueDate, disableToC, disableRestart, assignToAll.
 * @returns {Promise<Object>} The created assignment, populated with scenario and students
 */
export const createAssignment = (classroomId, assignmentData) =>
    api.post(`${BASE}/${classroomId}`, assignmentData).then((r) => r.data);

/**
 * Update an assignment with a partial set of fields.
 *
 * The roster follows the same rule as creation, and each affected student's activeAssignments
 * is kept in step. Omitting `assignToAll` leaves the assignment's existing setting alone.
 * @route PATCH /api/assignments/:assignmentId
 * @param {string} assignmentId
 * @param {Object} updateData - Any subset of Assignment fields
 * @returns {Promise<Object>} The updated assignment, populated with scenario and students
 */
export const updateAssignment = (assignmentId, updateData) =>
    api.patch(`${BASE}/${assignmentId}`, updateData).then((r) => r.data);

/**
 * Delete an assignment and remove it from each student's active assignments.
 * @route DELETE /api/assignments/:assignmentId
 * @param {string} assignmentId
 * @returns {Promise<{message: string}>} Deletion confirmation
 */
export const deleteAssignment = (assignmentId) =>
    api.delete(`${BASE}/${assignmentId}`).then((r) => r.data);

/**
 * Search a classroom's assignments by title (fuzzy autocomplete, max 10 results).
 * @route POST /api/assignments/searchClassroomAssignments/:classroomId
 * @param {string} classroomId
 * @param {string} searchTerm
 * @returns {Promise<{info: Object[]}>} Matching assignments under `info`
 */
export const searchClassroomAssignments = (classroomId, searchTerm) =>
    api.post(`${BASE}/searchClassroomAssignments/${classroomId}`, { searchTerm }).then((r) => r.data);
